import React, { useState } from 'react';

// Hooks
import useCreateCustomer from '@src/hooks/useCreateCustomer';
import useCustomerLogin from '@src/hooks/useCustomerLogin';

// Comps
import {CustomFields} from '@project-stories/ecommerce/Account/AccountFields/AccountFields';
import {InputField} from '@project-stories/elements/InputField/InputField';
import ButtonSpinner from '@project-stories/elements/ButtonSpinner/ButtonSpinner';
import {Link} from '@arctheme-components/elements/Link/Link';
import {Paragraph} from '@project-stories/elements/Paragraph/Paragraph';
import {Checkbox} from "@project-stories/elements/Checkbox/Checkbox";
import useCustomerAccessToken from "@src/hooks/useCustomerAccessToken";

type Props = {
    section: any;
    showCustomFields?: boolean;
    signUpFields?: any;
};

const AccountRegisterForm = ({ section, showCustomFields, signUpFields }: Props) => {
    // Extract settings from section (which is now the block settings)
    const {
        first_name_label = "First name",
        first_name_placeholder = "First name",
        last_name_label = "Last name", 
        last_name_placeholder = "Last name",
        email_label = "Email address",
        email_placeholder = "Email address",
        phone_label = "Phone number",
        phone_placeholder = "Phone number",
        area_code = "+61",
        show_phone_field = false,
        password_label = "Password",
        password_placeholder = "Password",
        password_confirm_label = "Confirm password",
        password_confirm_placeholder = "Confirm password",
        button_label_register = "Create account",
        button_style_register = "primary",
        button_label_already_registered = "Already have an account? Sign in",
        show_newsletter_signup = true,
        newsletter_label = "<p>Sign up for newsletter</p>",
        newsletter_default_checked = true,
        show_newsletter_disclaimer = true,
        newsletter_disclaimer = "<p>By selecting to join our mailing list you agree to our privacy policy.</p>",
        show_login_cta = true,
        login_cta_text = "Already have an account? Sign in",
        enable_social_login = true,
        title_register = "Create your account",
        tagline_register = "Create an account for an easy checkout, save addresses and wishlists and view your order history."
    } = section.block_settings;

    // Sort custom fields
    const customSignUpFields = signUpFields ? signUpFields.sort((a, b) => {
        const orderA = a.field_order ? parseInt(a.field_order) : Infinity;
        const orderB = b.field_order ? parseInt(b.field_order) : Infinity;
        return orderA - orderB;
    }) : [];

    // Hooks
    const {
        createCustomer,
        loading: createCustomerLoading,
        error: createCustomerError,
        success: createCustomerSuccess
    } = useCreateCustomer();
    const { loginCustomer, loading: loginLoading } = useCustomerLogin();
    const {
        getAccessToken,
        loading: accessLoading,
        errorMessage: accessError,
        successMessage: accessSuccess
    } = useCustomerAccessToken();

    // States
    const baseState = {
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        password_confirmation: '',
        ...(show_newsletter_signup && {
            email_opt_in: newsletter_default_checked || false
        }),
        ...(show_phone_field && {
            area_code: area_code || '+61',
            phone: ''
        })
    };

    const initialDependenciesState = Object.assign(
        {},
        baseState,
        ...customSignUpFields.map((field) => ({
            [field.metafield_key]: ''
        }))
    );
    const baseStateFilled = {
        first_name: false,
        last_name: false,
        email: false,
        password: false,
        password_confirmation: false,
        ...(show_newsletter_signup && {
            email_opt_in: true // Newsletter checkbox is optional, so always considered "filled"
        }),
        ...(show_phone_field && {
            area_code: true, // Area code has default value
            phone: false
        })
    };

    const initialDependenciesStateFilled = Object.assign(
        {},
        baseStateFilled,
        ...customSignUpFields.map((field) => ({
            [field.metafield_key]: field.is_required ? false : true
        }))
    );
    const baseStateHasError = {
        first_name: false,
        last_name: false,
        email: false,
        password: false,
        password_confirmation: false,
        ...(show_newsletter_signup && {
            email_opt_in: false
        }),
        ...(show_phone_field && {
            area_code: false,
            phone: false
        })
    };

    const initialDependenciesStateHasError = Object.assign(
        {},
        baseStateHasError,
        ...customSignUpFields.map((field) => ({
            [field.metafield_key]: false
        }))
    );
    const [dependenciesState, setDependenciesState]: any = useState(initialDependenciesState);
    const [dependenciesStateFilled, setDependenciesStateFilled]: any = useState(initialDependenciesStateFilled);
    const [dependenciesStateHasError, setDependenciesStateHasError]: any = useState(initialDependenciesStateHasError);

    // Handle input change and update global dependency state
    const handleInputChange = (e) => {
        const { name, value, type: inputType, checked } = e.target;

        // For checkboxes, use the checked value, otherwise use the input value
        let newValue = inputType === 'checkbox' ? checked : value;

        // Special handling for phone number - remove leading zero
        if (name === 'phone' && typeof newValue === 'string') {
            // Remove leading zero if present
            newValue = newValue.replace(/^0+/, '');
        }

        // Update the global dependency state
        updateDependencyState(name, newValue);

        // Check if the global dependency state is filled
        toggleDependencyStateFilled(name, newValue);
    };

    // Function to update the global dependencies state
    const updateDependencyState = (name, value) => {
        setDependenciesState((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };

    // Function to check if the global dependencies state has value
    const toggleDependencyStateFilled = (name, value) => {
        const customField = customSignUpFields.find((field) => name === field.metafield_key);
        const isFilled = customField && !customField.is_required ? true : !!value;


        setDependenciesStateFilled((prev) => {
            const updated = {
                ...prev,
                [name]: isFilled
            };
            return updated;
        });
    };

    const resetErrors = () => {
        let errors = {};

        for (const field in dependenciesStateHasError) {
            errors = {
                ...errors,
                [field]: false
            };
        }

        setDependenciesStateHasError(errors);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        resetErrors();

        // Validate the fields
        let errors = {};
        for (const field in dependenciesStateFilled) {
            errors = {
                ...errors,
                [field]: !dependenciesStateFilled[field]
            };
        }

        for (const error in errors) {
            if (errors[error]) {
                setDependenciesStateHasError(errors);

                return;
            }
        }

        // Set optIn fields based on user selection
        setDependenciesState((prevState) => ({
            ...prevState,
            ['optInSMS']: false,
            ['optInEmail']: dependenciesState.email_opt_in
        }));

        const createResponse = await createCustomer(dependenciesState);
        if (createResponse) {
            const email = dependenciesState.email;
            const password = dependenciesState.password;
            // Check if the pair of email and password exists in the system
            const data: any = await getAccessToken(email, password);
            // If exists, then sign in
            if (data.success) {
                // Log the customer in after successful creation
                await loginCustomer(email, password);
            }
        }
    };

    return (
        <form
            id='registerForm'
            onSubmit={handleSubmit}
            className='account-page__form'
        >
            <div className='flex flex-col gap-y-5 w-full'>
             
                {/* First Name */}
                <InputField
                    type='text'
                    className="w-full"
                    id='firstNameRegister'
                    name='first_name'
                    defaultValue={dependenciesState['first_name']}
                    eventHandlers={{
                        onChange: handleInputChange
                    }}
                    required={true}
                    label={first_name_label}
                    placeholder={`${first_name_placeholder}`}
                    // hasError={dependenciesStateHasError['first_name']}
                    // errorMessage={`${first_name_label} is required`}
                />

                {/* Last Name */}
                <InputField
                    type='text'
                    className="w-full"
                    id='lastNameRegister'
                    name='last_name'
                    defaultValue={dependenciesState['last_name']}
                    eventHandlers={{
                        onChange: handleInputChange
                    }}
                    required={true}
                    label={last_name_label}
                    placeholder={`${last_name_placeholder}`}
                    // hasError={dependenciesStateHasError['last_name']}
                    // errorMessage={`${last_name_label} is required`}
                />

                {/* Email */}
                <InputField
                    type='email'
                    className="w-full"
                    id='emailRegister'
                    name='email'
                    defaultValue={dependenciesState['email']}
                    eventHandlers={{
                        onChange: handleInputChange
                    }}
                    required={true}
                    label={email_label}
                    placeholder={`${email_placeholder}`}
                    // hasError={dependenciesStateHasError['email']}
                    // errorMessage={`${email_label} is required`}
                />

                {/* Phone */}
                {show_phone_field &&
                    <InputField
                        type='tel'
                        className="w-full"
                        id='phoneRegister'
                        name='phone'
                        defaultValue={dependenciesState['phone']}
                        eventHandlers={{
                            onChange: handleInputChange
                        }}
                        required={true}
                        label={phone_label}
                        placeholder={`${phone_placeholder}`}
                    />
                }

                {/* Password */}
                <InputField
                    type='password'
                    className="w-full"
                    id='passwordRegister'
                    name='password'
                    defaultValue={dependenciesState['password']}
                    eventHandlers={{
                        onChange: handleInputChange
                    }}
                    required={true}
                    label={password_label}
                    placeholder={`${password_placeholder}`}
                    // hasError={dependenciesStateHasError['password']}
                    // errorMessage={`${password_label} is required`}
                />

                {/* Confirm Password */}
                <InputField
                    type='password'
                    className="w-full"
                    id='passwordConfirmationRegister'
                    name='password_confirmation'
                    defaultValue={dependenciesState['password_confirmation']}
                    eventHandlers={{
                        onChange: handleInputChange
                    }}
                    required={true}
                    label={password_confirm_label}
                    placeholder={`${password_confirm_placeholder}`}
                    // hasError={dependenciesStateHasError['password_confirmation']}
                    // errorMessage={`${password_confirm_label} is required`}
                />

                {/* Custom Fields */}
                {showCustomFields && customSignUpFields.length > 0 && (
                    <CustomFields
                        customSignUpFields={customSignUpFields}
                        onChange={handleInputChange}
                        values={dependenciesState}
                        valuesError={dependenciesStateHasError}
                    />
                )}

                {createCustomerError && (
                    <Paragraph
                        type='errors text-center'
                        text={createCustomerError}
                    />
                )}

                {show_newsletter_signup && (
                    <Checkbox 
                        id="newsletter-signup-register"
                        name="email_opt_in"
                        classes="l3--medium"
                        label={<div dangerouslySetInnerHTML={{ __html: newsletter_label }} />}
                        checked={dependenciesState.email_opt_in || false}
                        onChange={handleInputChange}
                    />
                )}

                <ButtonSpinner
                    classes={`w-full button button-${button_style_register}`}
                    isLoading={createCustomerLoading || loginLoading}
                    loadingText={'Creating account...'}
                    ariaLabel='Submit registration form'
                >
                    {button_label_register}
                </ButtonSpinner>
                
                {show_newsletter_signup && show_newsletter_disclaimer && newsletter_disclaimer && (
                    <div 
                        className={`b4 account-page__form-terms`}
                        dangerouslySetInnerHTML={{ __html: newsletter_disclaimer }}
                    />
                )}

                <hr />
                
                {show_login_cta && (
                    <Link
                        link='/account/login'
                        classes='b4 link--underlined text-[#696969] text-center'
                        ariaLabel='Go to login page'
                    >
                        {login_cta_text}
                    </Link>
                )}

                {/* Social login app snippet */}
                {enable_social_login && <div className='one-click-social-login'></div>}
            </div>
        </form>
    );
};

export default AccountRegisterForm;
